// Import Thirdparty styles
import 'animate.css/animate.min.css'
import 'aos/dist/aos.css'
import 'flatpickr/dist/flatpickr.css'
import 'react-circular-progressbar/dist/styles.css'
import 'react-datepicker/dist/react-datepicker-cssmodules.css'
import 'react-datepicker/dist/react-datepicker.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-tooltip/dist/react-tooltip.css' // react-tooltip v5
import 'select2/dist/css/select2.css'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/scrollbar'

// Import custom style files
import '../public/assets/css/new-theme/feather/feather.css'
import '../public/assets/css/new-theme/newCustom.scss'
import '../public/assets/css/new-theme/newDaypicker.scss'
import '../public/assets/css/new-theme/newSchedule.scss'
import '../public/assets/css/new-theme/react-slick.scss'
import '../public/assets/css/new-theme/reviewGraph.scss'
import '../public/assets/css/new-theme/schedule-dropdown.scss'
import '../public/assets/css/new-theme/team.scss'

// Import Tailwind
import '../public/assets/css/new-theme/tailwind.css'

import { datadogLogs } from '@datadog/browser-logs'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { TOAST_CONTAINER_ID } from '@repo/design-system/index'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import AOS from 'aos'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Slide, ToastContainer, toast as toastType } from 'react-toastify'
import PopupProvider from '../components/basic/popup/PopupProvider'
import { extractUserTypeFromPath, initGTMScript } from '../helpers/gtmHelper'
import { EventRouterProvider } from '../modules/context/EventRouterContext'
import { LocaleProvider } from '../modules/context/LocaleContext'
import { UserProvider } from '../modules/context/UserContext'
import { IS_DEVELOPMENT, IS_PRODUCTION } from '../modules/envVars'
import useAppRouter, { CustomRouter } from '../modules/hooks/common/useAppRouter'
import useIsMobile from '../modules/hooks/common/useIsMobile'
import { SNACKBAR_CONTAINER_ID } from '../modules/hooks/common/useSnackbar'
import { LOCALE_KO } from '../modules/i18n/config'
import urls from '../modules/urls'
import { setAdHistory, setReferralCode, setVisitUtm } from '../modules/utils'
import { GOOGLE_CLIENT_ID, NEXT_PUBLIC_SENTRY_DSN } from '../modules/vars'

declare global {
  interface Window {
    dataLayer: any
  }
}

const ignorableErrors = [
  'The voice you passed is not valid or the voices have not been loaded yet.',
  '_next/static/chunks',
  // NextJS - next-head-count error
  'TypeError: Cannot read properties of null (reading "content")',
  `TypeError: Cannot read properties of null (reading 'content')`,
  `Cannot read properties of null (reading 'content')`,
  '_next/static/chunks',
  'Non-Error promise rejection captured',
]

/**
 * React Query Devtools in production
 * NODE_ENV === 'production' 이어도 볼 수 있도록 처리
 * https://tanstack.com/query/v4/docs/react/devtools#devtools-in-production
 */
const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/lib/index.prod.js').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
)

function MyApp({ Component, pageProps }: AppProps) {
  const router = useAppRouter()

  /**
   * App.tsx State
   */
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      })
  )

  const isMobile = useIsMobile()

  /**
   * React Query Devtools in production
   * NODE_ENV === 'production' 이어도 볼 수 있도록 처리
   * https://tanstack.com/query/v4/docs/react/devtools#devtools-in-production
   */
  const [showDevtools, setShowDevtools] = useState<boolean>(false)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  /**
   * 윈도우가 있을 때, 모웹에서 로그인 된 상태로 포털로 접근하는 경우 포털 -> 이동 이후 앱 유도 프로세스
   * 로그인 안된 상태인 경우 다른 로직에 의해서 리다이렉트
   */
  useEffect(() => {
    if (typeof window !== 'undefined' && router.isReady) {
      if (
        !router.pathname.startsWith('/[lang]/student/portal/pricing') &&
        !router.pathname.startsWith('/[lang]/student/portal/promotion') &&
        !router.pathname.startsWith('/[lang]/student/portal/lesson/review/evaluation') &&
        !router.pathname.startsWith('/[lang]/student/portal/trial') &&
        !router.pathname.startsWith('/[lang]/student/portal/challenge') &&
        !router.pathname.startsWith('/[lang]/student/portal/invite-event') &&
        !router.pathname.startsWith('/[lang]/student/portal/lessons/bite-sized-feedback') &&
        !router.pathname.startsWith('/[lang]/student/portal/notice') &&
        !router.pathname.startsWith('/[lang]/student/portal/materials/ad') &&
        !router.pathname.startsWith('/[lang]/student/portal/home') &&
        !router.pathname.startsWith('/[lang]/student/portal/welcome') &&
        !router.pathname.startsWith('/[lang]/student/portal/study-club') &&
        router.pathname.startsWith('/[lang]/student/portal')
      ) {
        if (isMobile) {
          router.push(`/${router.query.lang}${urls.student.newPortal.home}`)
        }
      }
    }
  }, [router, isMobile])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setAdHistory(router.query)
      setVisitUtm(router.query)
      initGTMScript(extractUserTypeFromPath(router.asPath))
      setReferralCode(router.query)
    }
  }, [])

  /**
   * AOS animation library
   */
  const initAOS = () => {
    AOS.init({
      duration: 500,
      easing: 'ease-out-quad',
      once: true,
      startEvent: 'load',
    })
  }

  useEffect(() => {
    if (process.browser) {
      initAOS()
    }

    window.dispatchEvent(new Event('resize'))
  }, [])

  useEffect(() => {
    // Sentry
    const domain = 'www.ringleplus.com'
    const RELEASE_VERSION = process.env.GIT_COMMIT_SHA

    Sentry.init({
      enabled: process.env.NODE_ENV === 'production',

      dsn: NEXT_PUBLIC_SENTRY_DSN,
      environment: window?.location?.host === domain ? 'production' : 'qa',
      release: RELEASE_VERSION,
      ignoreErrors: [
        // 'TypeError: Cannot read properties of null (reading "content")',
        // `TypeError: Cannot read properties of null (reading 'content')`,
        // /Cannot read properties of null \(reading 'content'\)/,
        // '_next/static/chunks',
      ],

      // This enables automatic instrumentation (highly recommended), but is not
      // necessary for purely manual usage
      integrations: [
        new Integrations.BrowserTracing({
          tracePropagationTargets: [],
        }),
      ],
      // To set a uniform sample rate
      tracesSampleRate: 0,
      denyUrls: ['local.ringleplus.com'],
      beforeSend(event, hint) {
        const error = hint.originalException
        if (typeof error == 'string') {
          ignorableErrors.map((item, i) => {
            if (error.includes(item)) {
              return null
            }
          })
        } else if (error instanceof Error) {
          ignorableErrors.map((item, i) => {
            if (error.message.includes(item)) {
              return null
            }
          })
        }
        return event
      },
    })

    const logClientToken = 'pub10a8045aeb5d7ea45141fae5dad3f955'
    const datadogServiceName = 'ringle-plus-web'

    if (datadogLogs.getInitConfiguration()?.service !== datadogServiceName) {
      datadogLogs.init({
        clientToken: logClientToken,
        site: 'datadoghq.com',
        env: IS_DEVELOPMENT ? 'development' : window?.location?.host === domain ? 'production' : 'qa',
        service: datadogServiceName,
        version: RELEASE_VERSION,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: 'all',
        forwardReports: 'all',
        sampleRate: 100,
        beforeSend: (log) => {
          if (log.message.includes('Braze')) {
            return false
          }
        },
      })
    }
  }, [])

  const toastProps = {
    containerId: TOAST_CONTAINER_ID,
    position: toastType.POSITION.BOTTOM_CENTER,
    className: 'new-design-system',
    closeButton: false,
    autoClose: 3000,
    hideProgressBar: true,
    newestOnTop: true,
    closeOnClick: true,
    rtl: false,
    draggable: true,
    transition: Slide,
    pauseOnHover: true,
    enableMultiContainer: true,
  }

  const snackbarProps = {
    containerId: SNACKBAR_CONTAINER_ID,
    position: isMobile ? toastType.POSITION.TOP_CENTER : toastType.POSITION.TOP_RIGHT,
    autoClose: false as const,
    hideProgressBar: true,
    newestOnTop: true,
    closeOnClick: true,
    draggable: true,
    progress: undefined,
    enableMultiContainer: true,
  }

  const isSafari = useRef(false)

  useEffect(() => {
    if (navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1) {
      isSafari.current = true
    }
  }, [])

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="style"
          crossOrigin=""
          href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.6/dist/web/variable/pretendardvariable-dynamic-subset.css"
        />
        <link rel="preconnect" href="https://cdn.jsdelivr.net" crossOrigin="anonymous" />
        <link rel="icon" href={`/assets/favicon_adult.ico`} type="image/x-icon" />
        {isSafari && (
          <link
            rel="stylesheet"
            crossOrigin=""
            href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.6/dist/web/variable/pretendardvariable-dynamic-subset.css"
          />
        )}
        {shouldNotBeIndexed(router) && <meta name="robots" content="noindex" />}
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps?.dehydratedState}>
          <UserProvider>
            <LocaleProvider>
              <EventRouterProvider>
                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                  <PopupProvider />
                  <Component {...pageProps} />
                  <ToastContainer {...toastProps} /> {/* For 3.0 Design System Toast */}
                  <ToastContainer {...snackbarProps} /> {/* For 3.0 Design System SnackBar */}
                  <div id="ds-bottom-sheet"></div> {/* For 3.0 Design System Bottom Sheet */}
                  <div id="ds-modal"></div> {/* For 3.0 Design System Popup */}
                </GoogleOAuthProvider>
              </EventRouterProvider>
            </LocaleProvider>
          </UserProvider>
          {!IS_PRODUCTION && showDevtools && (
            <Suspense fallback={null}>
              <ReactQueryDevtoolsProduction initialIsOpen panelProps={{ className: 'min-h-[300px]' }} />
            </Suspense>
          )}
        </Hydrate>
      </QueryClientProvider>
    </>
  )
}

export default MyApp

export const redirectRoot = (ctx, headerObj = {}) => {
  const { lang = LOCALE_KO } = ctx.query
  if (ctx.res) {
    ctx.res.writeHead(302, {
      Location: `/${lang}/`,
      ...headerObj,
    })
    ctx.res.end()
  }
}

const noIndexList = [/^\/\[lang\]\/student\/auth\/.*/]

const shouldNotBeIndexed = (router: CustomRouter) => {
  if (noIndexList.some((regex) => regex.test(router.pathname))) {
    return true
  }
  return false
}
